import {
  applyFirmBulkAssignee as applyFirmBulkAssigneeApi,
  createFirm as createFirmApi,
  deleteFirm as deleteFirmApi,
  getFirm as getFirmApi,
  listFirms as listFirmsApi,
  patchFirm as patchFirmApi,
  previewFirmBulkAssignee as previewFirmBulkAssigneeApi,
} from '@shared/api/firms';
import { type Paged, parsePaged } from '@shared/api/paging';
import type {
  FirmBulkAssigneeRequest,
  FirmBulkAssigneeResult,
  FirmCreateRequest,
  FirmListQuery,
  FirmPatchRequest,
} from '@shared/types/firm';
import type { Firm } from '@domain/firm/model';
import { parseFirm } from '@domain/firm/parseFirm';

interface ListState {
  data: Paged<Firm> | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

export const firmListStore = {
  get data(): Paged<Firm> | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  /// Refresh the list. Each call gets a monotonically increasing token and
  /// only the response for the latest token is applied, so a fast filter
  /// change followed by a slow API call cannot overwrite the newer state.
  async refresh(query: FirmListQuery): Promise<void> {
    const token = ++listToken;
    listState.loading = true;
    listState.error = null;
    try {
      const raw = await listFirmsApi(query);
      if (token !== listToken) return;
      listState.data = parsePaged(raw, parseFirm);
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
};

interface DetailState {
  data: Firm | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({ data: null, loading: false, error: null });
let detailToken = 0;

export const firmDetailStore = {
  get data(): Firm | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  /// Load the firm with the given id. Stale responses (from a previously
  /// requested id whose fetch is still in flight) are dropped on the floor.
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const raw = await getFirmApi(id);
      if (token !== detailToken) return;
      detailState.data = parseFirm(raw);
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(firm: Firm): void {
    detailState.data = firm;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

/// 関連事務所 (所属事務所) を 1 件取得するための軽量ストア。
///
/// 弁護士詳細ページのヘッダーで所属事務所名を常時リンク表示し、営業対象外/競合の
/// 注意判定にも使う。事務所詳細ページが使う `firmDetailStore` と状態を共有すると
/// 取り違えるため、別インスタンスにしている。`firmId` が null のときは空状態。
interface AffiliatedFirmState {
  data: Firm | null;
  loading: boolean;
  error: string | null;
}

const affiliatedFirmState = $state<AffiliatedFirmState>({
  data: null,
  loading: false,
  error: null,
});
let affiliatedFirmToken = 0;

export const affiliatedFirmStore = {
  get data(): Firm | null {
    return affiliatedFirmState.data;
  },
  get loading(): boolean {
    return affiliatedFirmState.loading;
  },
  get error(): string | null {
    return affiliatedFirmState.error;
  },
  /// 関連事務所をロードする。firmId 未設定なら空状態にする。stale は token で破棄。
  async load(firmId: string | null): Promise<void> {
    const token = ++affiliatedFirmToken;
    if (!firmId) {
      affiliatedFirmState.data = null;
      affiliatedFirmState.loading = false;
      affiliatedFirmState.error = null;
      return;
    }
    affiliatedFirmState.data = null;
    affiliatedFirmState.loading = true;
    affiliatedFirmState.error = null;
    try {
      const firm = parseFirm(await getFirmApi(firmId));
      if (token !== affiliatedFirmToken) return;
      affiliatedFirmState.data = firm;
    } catch (err) {
      if (token !== affiliatedFirmToken) return;
      affiliatedFirmState.data = null;
      affiliatedFirmState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === affiliatedFirmToken) affiliatedFirmState.loading = false;
    }
  },
  clear(): void {
    affiliatedFirmToken++;
    affiliatedFirmState.data = null;
    affiliatedFirmState.loading = false;
    affiliatedFirmState.error = null;
  },
};

/// 事務所マスタを「選択肢」用に全件ロードするストア。
///
/// 弁護士詳細 (`LawyerDetail`) の所属事務所 inline select と、事務所詳細 (G2 / #483) の
/// picker が再利用する。`firmListStore` はページネーション付きで都度上書きされ、
/// `firmDetailStore` / `affiliatedFirmStore` は単一事務所用なので、選択肢解決には別
/// インスタンスを使う。`listFirms` は `unknown` を返し、`Firm` は業務モデルなので
/// `parsePaged(raw, parseFirm)` の門番で検証する (#475)。失敗は strict throw → store が
/// catch して error に倒し、選択肢は空のままにする。
interface FirmOptionsState {
  data: Firm[];
  loading: boolean;
  error: string | null;
}

const firmOptionsState = $state<FirmOptionsState>({ data: [], loading: false, error: null });
let firmOptionsToken = 0;

export const firmOptionsStore = {
  get data(): Firm[] {
    return firmOptionsState.data;
  },
  get loading(): boolean {
    return firmOptionsState.loading;
  },
  get error(): string | null {
    return firmOptionsState.error;
  },
  /// 事務所マスタを official_name 昇順で全件 (最大 500) ロードする。stale は token で破棄。
  async load(): Promise<void> {
    const token = ++firmOptionsToken;
    firmOptionsState.loading = true;
    firmOptionsState.error = null;
    try {
      const raw = await listFirmsApi({
        page_size: 500,
        sort_by: 'official_name',
        sort_dir: 'asc',
      });
      if (token !== firmOptionsToken) return;
      firmOptionsState.data = parsePaged(raw, parseFirm).items;
    } catch (err) {
      if (token !== firmOptionsToken) return;
      firmOptionsState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === firmOptionsToken) firmOptionsState.loading = false;
    }
  },
  clear(): void {
    firmOptionsToken++;
    firmOptionsState.data = [];
    firmOptionsState.loading = false;
    firmOptionsState.error = null;
  },
};

export async function createFirm(body: FirmCreateRequest): Promise<Firm> {
  return parseFirm(await createFirmApi(body));
}

export async function patchFirm(id: string, body: FirmPatchRequest): Promise<Firm> {
  const updated = parseFirm(await patchFirmApi(id, body));
  if (detailState.data?.id === id) {
    detailState.data = updated;
  }
  return updated;
}

export async function deleteFirm(id: string): Promise<void> {
  await deleteFirmApi(id);
  if (detailState.data?.id === id) {
    detailState.data = null;
  }
}

export function previewFirmBulkAssignee(
  body: FirmBulkAssigneeRequest,
): Promise<FirmBulkAssigneeResult> {
  return previewFirmBulkAssigneeApi(body);
}

export function applyFirmBulkAssignee(
  body: FirmBulkAssigneeRequest,
): Promise<FirmBulkAssigneeResult> {
  return applyFirmBulkAssigneeApi(body);
}
